import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { TemplaterAPI } from '@saraa/templater/api/TemplaterAPI.js';
import type { GenerationLog } from '@saraa/templater/entities/GenerationLog.js';
import { GenerationLogStatus } from '@saraa/templater/entities/GenerationLog.js';
import type { GenerationTask } from '@saraa/templater/entities/GenerationTask.js';
import { GenerationTaskStatus } from '@saraa/templater/entities/GenerationTask.js';
import { generationResultFileName } from '@saraa/templater/generationFileName.js';
import { format } from 'date-fns/format';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import { isPast } from 'date-fns/isPast';
import { html, nothing, type TemplateResult } from 'lit';
import { dialogHeader, emptyState, errorText, sectionLoader } from './ui.js';

/**
 * Runs this tab queued, and results it has already pushed at the user.
 *
 * Both are in memory and deliberately not persisted: they decide nothing about *what is shown* — the task
 * list and the history both come from the server — only whether a finished result gets handed over without
 * a click, which is a property of the tab the user pressed the button in and of no interest to any other.
 */
const startedHere = new Set<string>();
const autoDownloaded = new Set<string>();

/** How much history the dialog asks for at once, and how much every press of "Load more" adds on top. */
const LOGS_PAGE_SIZE = 20;

export function markGenerationStartedHere(taskId: string): void {
  startedHere.add(taskId);
}

const downloadResult = async (log: GenerationLog) => {
  if (!log.resultZipFileId) return;
  const file = await AdornisFile.getDownloadLink(log.resultZipFileId);
  /** Named from the run it came out of, so a folder full of these still says which is which. */
  await downloadLink(file, generationResultFileName(log.finishedAt ? new Date(log.finishedAt) : undefined));
};

/**
 * Fixed patterns rather than the browser's locale, so the times a user reads here match the ones the server
 * writes into log lines and file names — a run someone is asked to quote in a support mail should not depend
 * on which machine they happened to open it from. Day-first, matching the DD/MM/YYYY the templates use.
 */
const TIME_PATTERN = 'HH:mm';
const DAY_TIME_PATTERN = 'dd/MM HH:mm';
const DATE_TIME_PATTERN = 'dd/MM/yyyy HH:mm';

const formatTime = (value: Date | string | null | undefined) => (value ? format(new Date(value), TIME_PATTERN) : '');

const formatExpiry = (value: Date | string | null | undefined) =>
  value ? format(new Date(value), DAY_TIME_PATTERN) : '';

/**
 * Compact on purpose — these sit inline in a row title, where `2m 05s` reads at a glance and
 * "2 minutes 5 seconds" does not.
 */
const formatDuration = (ms: number | null | undefined) => {
  if (!ms || ms < 0) return '';

  /** Rounded before splitting, so a run of 1999ms reads as `2s` rather than being truncated to `1s`. */
  const {
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
  } = intervalToDuration({ start: 0, end: Math.round(ms / 1000) * 1000 });
  const pad = (value: number) => String(value).padStart(2, '0');

  /** Days folded into hours: a run that sat in the queue overnight should read `26h`, not lose a day. */
  const totalHours = days * 24 + hours;

  if (totalHours > 0) return `${totalHours}h ${pad(minutes)}m`;
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`;
  return `${seconds}s`;
};

const formatDateTime = (value: Date | string | null | undefined) =>
  value ? format(new Date(value), DATE_TIME_PATTERN) : '—';

const isExpired = (value: Date | string | null | undefined) => !!value && isPast(new Date(value));

/** Stack traces arrive as one long blob — the list shows the headline, the expanded panel shows all of it. */
const firstLine = (message: string) => message.split('\n')[0] ?? message;

interface RowOptions {
  icon: TemplateResult;
  title: string;
  detail: TemplateResult | string;
  /** Rendered full-width under the detail line — the progress bar of a running task. */
  meter?: TemplateResult;
  trailing?: TemplateResult;
  expanded?: boolean;
  onToggle?: () => void;
  details?: TemplateResult;
}

const rowBody = (options: RowOptions) => html`
  <span class="flex size-9 shrink-0 items-center justify-center">${options.icon}</span>
  <span class="flex min-w-0 flex-1 flex-col gap-1">
    <span class="text-base-content truncate text-base font-medium">${options.title}</span>
    <span class="text-base-content/70 truncate text-sm">${options.detail}</span>
    ${options.meter ?? nothing}
  </span>
`;

const row = (options: RowOptions) => html`
  <li class="border-base-300 bg-base-100 rounded-box flex flex-col border">
    <div class="flex items-center gap-3 p-4">
      ${options.onToggle
        ? html`<button
            type="button"
            class="hover:bg-base-200 -m-1 flex min-w-0 flex-1 items-center gap-3 rounded p-1 text-left"
            aria-expanded=${options.expanded ? 'true' : 'false'}
            @click=${options.onToggle}
          >
            ${rowBody(options)}
            <span
              class="icon-[material-symbols--expand-more] text-base-content/60 ${options.expanded
                ? 'rotate-180'
                : ''} shrink-0 text-xl"
              aria-hidden="true"
            ></span>
          </button>`
        : rowBody(options)}
      ${options.trailing ?? nothing}
    </div>
    ${options.expanded && options.details
      ? html`<div class="border-base-300 border-t p-4">${options.details}</div>`
      : nothing}
  </li>
`;

const detailItem = (label: string, value: string) => html`
  <div class="flex flex-col gap-1">
    <dt class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">${label}</dt>
    <dd class="text-base-content text-sm">${value}</dd>
  </div>
`;

/**
 * The full failure, verbatim and copyable — this is what the user forwards when they ask why a run died, so
 * it is never truncated and it wraps rather than scrolling sideways.
 */
const errorBlock = (message: string) => html`
  <div class="mt-4 flex flex-col gap-1">
    <div class="flex items-center justify-between gap-2">
      <span class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">Error</span>
      <button
        type="button"
        class="btn btn-ghost btn-sm gap-1"
        @click=${() => void navigator.clipboard.writeText(message)}
      >
        <span class="icon-[material-symbols--content-copy-outline] text-base"></span> Copy
      </button>
    </div>
    <pre
      class="bg-base-200 text-error rounded-box max-h-72 overflow-auto p-3 text-sm leading-relaxed whitespace-pre-wrap"
    >
${message}</pre
    >
  </div>
`;

const logDetails = (log: GenerationLog) => {
  const expired = isExpired(log.resultExpiresAt);
  const availability = !log.resultZipFileId ? '—' : expired ? 'Expired' : `Until ${formatExpiry(log.resultExpiresAt)}`;

  return html`
    <dl class="grid grid-cols-2 gap-3 sm:grid-cols-3">
      ${detailItem('Started', formatDateTime(log.startedAt))} ${detailItem('Finished', formatDateTime(log.finishedAt))}
      ${detailItem('Duration', formatDuration(log.durationMs) || '—')}
      ${detailItem('Templates', String(log.templateCount ?? 0))}
      ${detailItem('Documents', String(log.documentCount ?? 0))} ${detailItem('Download', availability)}
    </dl>
    ${log.errorMessage ? errorBlock(log.errorMessage) : nothing}
    <p class="text-base-content/50 mt-4 font-mono text-xs break-all">Task ${log.taskId ?? '—'}</p>
  `;
};

const taskRow = (task: GenerationTask) => {
  const status = task.status ?? GenerationTaskStatus.QUEUED;

  if (status === GenerationTaskStatus.ERROR || status === GenerationTaskStatus.ABORTED) {
    return row({
      icon: html`<span class="icon-[material-symbols--error-outline] text-error text-2xl"></span>`,
      title: status === GenerationTaskStatus.ABORTED ? 'Cancelled' : 'Failed',
      detail: task.errorMessage ?? 'The generation did not finish.',
    });
  }

  const running = status === GenerationTaskStatus.RUNNING;
  const total = task.progressTotal ?? 0;
  const current = task.progressCurrent ?? 0;

  return row({
    icon: html`<span class="loading loading-spinner loading-md text-primary"></span>`,
    title: running ? 'Generating' : 'Waiting for a worker',
    detail: task.progress ?? (running ? 'Working on your documents…' : 'Queued — a worker will pick this up shortly.'),
    /**
     * Only the phases that count their own steps get a bar. The rest stay on the spinner rather than
     * pretending to a precision they do not have.
     */
    meter:
      total > 0
        ? html`<progress
            class="progress progress-primary h-1.5 w-full"
            value=${current}
            max=${total}
            aria-label="Generation progress"
          ></progress>`
        : undefined,
    trailing:
      total > 0
        ? html`<span class="text-base-content/70 shrink-0 text-sm tabular-nums">${current}/${total}</span>`
        : (task.attempts ?? 0) > 1
          ? html`<span class="badge badge-ghost shrink-0">attempt ${task.attempts}</span>`
          : undefined,
  });
};

const logRow = (log: GenerationLog, expanded: boolean, onToggle: () => void) => {
  const shared = { expanded, onToggle, details: logDetails(log) };

  if (log.status === GenerationLogStatus.ERROR) {
    return row({
      ...shared,
      icon: html`<span class="icon-[material-symbols--error-outline] text-error text-2xl"></span>`,
      title: `Failed at ${formatTime(log.finishedAt)}`,
      detail: firstLine(log.errorMessage ?? 'The generation did not finish.'),
    });
  }

  const expired = isExpired(log.resultExpiresAt);
  const templates = `${log.templateCount ?? 0} template${log.templateCount === 1 ? '' : 's'}`;
  const duration = formatDuration(log.durationMs);

  return row({
    ...shared,
    icon: html`<span class="icon-[material-symbols--check-circle] text-success text-2xl"></span>`,
    title: `${formatTime(log.finishedAt)} — ${templates}${duration ? ` in ${duration}` : ''}`,
    detail: expired
      ? 'Download expired — generate again to get a new copy.'
      : `Available until ${formatExpiry(log.resultExpiresAt)}`,
    trailing:
      log.resultZipFileId && !expired
        ? html`<button type="button" class="btn btn-primary shrink-0 gap-1.5" @click=${() => void downloadResult(log)}>
            <span class="icon-[material-symbols--download] text-lg"></span> Zip
          </button>`
        : html`<span class="icon-[material-symbols--schedule] text-base-content/50 shrink-0 text-xl"></span>`,
  });
};

/**
 * Everything the user has scheduled: what is in the queue right now, and what came out of the runs before.
 *
 * The two halves come from genuinely different places — the live half is the task queue, the history is the
 * generation log — and a task moves from one to the other when its log turns up.
 */
const GenerateTasksDialog = stateful((props: { close: () => void }) => {
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null, []);
  const [logLimit, setLogLimit] = useState(LOGS_PAGE_SIZE, []);

  /**
   * No ids passed: the queue knows whose runs these are. Nothing here has to be told what to follow, and
   * nothing can go stale, because the list *is* the queue.
   */
  const tasksQuery = useQuery(
    () =>
      TemplaterAPI.subscribeTasks()({
        taskId: 1,
        status: 1,
        progress: 1,
        progressCurrent: 1,
        progressTotal: 1,
        attempts: 1,
        errorMessage: 1,
      }),
    [],
  );

  /**
   * "Load more" widens the same subscription rather than stitching pages together: the history stays one
   * live, newest-first list, so a run that finishes while the user is reading lands in the right place
   * instead of being appended to the end of a frozen page.
   */
  const logsQuery = useQuery(
    () =>
      TemplaterAPI.subscribeLogs(logLimit)({
        _id: 1,
        taskId: 1,
        status: 1,
        startedAt: 1,
        finishedAt: 1,
        durationMs: 1,
        templateCount: 1,
        documentCount: 1,
        resultZipFileId: 1,
        resultExpiresAt: 1,
        errorMessage: 1,
      }),
    [logLimit],
  );

  /**
   * Widening resubscribes, and a fresh subscription reports itself as loading with nothing in hand. Holding
   * on to the last list keeps the history on screen across that gap — pressing "Load more" should add rows,
   * not blank the section and bring them all back.
   */
  const lastLogs = useMemo(() => [{ value: [] as GenerationLog[] }], []);
  if (logsQuery.value) lastLogs.value = logsQuery.value as GenerationLog[];

  const running = (tasksQuery.value ?? []) as GenerationTask[];
  const logs = lastLogs.value;

  const loadingMoreLogs = logsQuery.loading && logs.length > 0;
  /**
   * A full page back means the server may well have more; a short one is the end of the history. While a
   * widen is still in flight the list is a page behind the limit it was asked for, so the button stays —
   * it is the thing the user just pressed, and it says so rather than vanishing under the cursor.
   */
  const hasMoreLogs = loadingMoreLogs || logs.length >= logLimit;

  /** Hand over the result of a run the user is sitting here waiting for, without making them click again. */
  useEffect(() => {
    for (const log of logs) {
      if (log.status !== GenerationLogStatus.DONE) continue;
      if (!log.taskId || !log.resultZipFileId) continue;
      if (!startedHere.has(log.taskId) || autoDownloaded.has(log.taskId)) continue;
      if (isExpired(log.resultExpiresAt)) continue;
      autoDownloaded.add(log.taskId);
      void downloadResult(log);
    }
  }, [logs.map(log => log.taskId).join(',')]);

  return html`
    <div class="flex min-h-0 flex-col gap-4">
      ${dialogHeader({
        title: 'Your generations',
        description: 'Runs keep going if you close this — reopen it any time to check on them.',
        close: props.close,
      })}

      <!--
        \`shrink-0\`, not \`min-h-0\`. This section is a flex child beside a \`flex-1\` history, so
        \`min-h-0\` let it shrink below its own content — and its content does not shrink back,
        so the empty state overflowed the section by ~67px and painted straight over the
        "Earlier" heading below it. That happened whenever nothing was running, which is the
        usual case. The queue list caps itself at 30vh, so refusing to shrink is bounded.
      -->
      <section class="flex shrink-0 flex-col gap-2">
        <h4 class="text-base-content/60 shrink-0 text-sm font-semibold tracking-wide uppercase">In progress</h4>
        ${running.length === 0
          ? emptyState('Nothing running right now.')
          : /**
             * The queue scrolls on its own and takes at most a third of the screen: someone who queued a
             * dozen runs still gets to see their history without scrolling past all of them first.
             */
            html`<ul class="flex max-h-[30vh] flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
              ${running.map(task => taskRow(task))}
            </ul>`}
        ${tasksQuery.error ? errorText(`Lost track of the running tasks: ${tasksQuery.error.message}`) : nothing}
      </section>

      <section class="flex min-h-0 flex-1 flex-col gap-2">
        <h4 class="text-base-content/60 shrink-0 text-sm font-semibold tracking-wide uppercase">Earlier</h4>
        ${logsQuery.loading && logs.length === 0
          ? sectionLoader()
          : logsQuery.error && logs.length === 0
            ? errorText(`Could not load your history: ${logsQuery.error.message}`)
            : logs.length === 0
              ? emptyState('No finished generations yet.')
              : /**
                 * The history takes whatever height is left over and scrolls inside it, and "Load more" sits
                 * below that scroll rather than in it: another page adds rows to scroll through instead of
                 * pushing the dialog past the screen, and the button stays where the user just clicked it.
                 */
                html`<div class="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
                    <ul class="flex flex-col gap-2">
                      ${logs.map(log =>
                        logRow(log, expandedLogId === log._id, () =>
                          setExpandedLogId(expandedLogId === log._id ? null : (log._id ?? null)),
                        ),
                      )}
                    </ul>
                  </div>
                  ${hasMoreLogs
                    ? html`<button
                        type="button"
                        class="btn btn-ghost btn-block shrink-0"
                        ?disabled=${loadingMoreLogs}
                        @click=${() => setLogLimit(logLimit + LOGS_PAGE_SIZE)}
                      >
                        ${loadingMoreLogs
                          ? html`<span class="loading loading-spinner loading-sm"></span> Loading…`
                          : 'Load more'}
                      </button>`
                    : nothing}
                  ${logsQuery.error ? errorText(`Could not load more: ${logsQuery.error.message}`) : nothing}`}
      </section>
    </div>
  `;
});

/**
 * Opens the task dialog. Resolves when the user closes it — there is nothing to hand back, the runs it shows
 * outlive it either way.
 */
export async function openGenerateTasksDialog(): Promise<void> {
  await showDialog<void>({
    content: resolve => GenerateTasksDialog({ close: () => resolve() }),
    /**
     * A plain `.modal-box` has no height limit of its own — only daisyUI's positioned variants bring one —
     * so a long history grows the box until the modal, which clips, simply cuts the top and bottom off. The
     * cap (daisyUI's own, in dvh so a mobile URL bar does not eat the footer) turns the box into a fixed
     * frame, and the column inside it hands the leftover height to the two lists to scroll in.
     */
    modalBoxClasses: 'flex max-h-[calc(100dvh_-_5em)] max-w-3xl flex-col overflow-hidden',
  });
}
